/**
 * Results toolbar for `DataViewTable`.
 *
 * Two zones inside one row:
 *   LEFT  - "Results" heading + ActiveFilterChips
 *   RIGHT - count + rows-per-page + pagination + rightSlot
 *
 * Inspector embedded + fullscreen variants make this toolbar sticky to
 * the outer scroll container via `toolbarStickyTop`. Platform variant
 * renders the same content non-sticky.
 *
 * Page / rows-per-page changes are emitted via `onPageChange` /
 * `onRowsPerPageChange` so the parent can compose its own side-effects
 * (e.g. scroll-into-view, server refetch).
 */
import React from 'react'
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ActiveFilterChips, {
  type ActiveFilterChip,
} from '../../inspector/shared/ActiveFilterChips'
import { poolwerxColors } from '../../../theme/colors'
import { TOOLBAR_DIVIDER } from '../../../theme/exceptionTableStyles'
import { Z_TOOLBAR } from './stickyOffsets'

export interface ResultsToolbarProps {
  isInspector: boolean
  /**
   * `top:` for the sticky toolbar (CSS length expression). May resolve
   * via `var(--app-chrome-height, ...)` so the toolbar tracks chrome
   * collapse without a re-render. Ignored when `isInspector` is false
   * (platform variant renders non-sticky inside its own card chrome).
   */
  toolbarStickyTop: string
  chips: ActiveFilterChip[]
  /**
   * Optional heading that replaces the default "Results" label on the
   * inspector variant. Use to surface the page/section title inline
   * with the toolbar instead of a separate page-header block above.
   * Falls back to "Results" when omitted. Ignored on the platform
   * variant (which renders no left-zone heading).
   */
  title?: React.ReactNode
  /**
   * Optional content rendered in the LEFT zone before the active-filter
   * chips. Used by the platform variant to surface per-group titles
   * (e.g. "Duplicate Sales · 1094") inline with the toolbar instead of
   * spending a separate row above the table.
   */
  leftSlot?: React.ReactNode
  totalCount: number
  rowsPerPage: number
  /** Zero-based current page. */
  page: number
  pageCount: number
  pageSizeOptions: number[]
  showPagination: boolean
  rightSlot: React.ReactNode | null
  isLoading: boolean
  onRowsPerPageChange: (rowsPerPage: number) => void
  /** Zero-based target page. */
  onPageChange: (page: number) => void
  /**
   * Open/Closed lifecycle pill rendered inline with the Results title,
   * before the pagination zone. Treats lifecycle as a view switch on
   * the table — not a filter — so it stays in lock-step with the sticky
   * toolbar and is always reachable while the user scrolls the body.
   * Pass ``null`` / omit on routes where the envelope can't serve a
   * Closed slice (live-classifier views) so the pill disappears.
   */
  statusToggle?: React.ReactNode
  /**
   * Compact "Oldest open Xd" KPI chip. Sits inline after the lifecycle
   * pill, before the pagination zone. Omit when the response has no
   * open items to age (closed view / empty envelope) — caller decides;
   * the toolbar just renders what it's given.
   */
  oldestOpenChip?: React.ReactNode
}

// Subtle hairline between control groups. Used only before the expand
// button per the spec ("small vertical divider before it") — the rest
// of the right zone reads as a set of grouped controls separated by
// generous gap, not by visible rules.
const ControlGroupDivider: React.FC = () => (
  <Divider
    orientation="vertical"
    flexItem
    sx={{
      height: 20,
      alignSelf: 'center',
      borderColor: 'rgba(0, 0, 0, 0.12)',
      mx: 0.25,
    }}
  />
)

// Compact pagination control: prev/next chevrons flanking `Page X of Y`,
// with first/last only when there are 3+ pages. Buttons keep a 32×32
// hit area regardless of the icon size; ends disable softly (low
// opacity, no hover lift) so the row alignment stays put without
// drawing the eye to dead controls.
const PAGINATION_BUTTON_SX = {
  width: 32,
  height: 32,
  borderRadius: 1,
  color: 'text.secondary',
  '&:hover': { backgroundColor: 'action.hover' },
  '&.Mui-disabled': {
    opacity: 0.35,
    color: 'text.secondary',
    backgroundColor: 'transparent',
  },
}

const CompactPagination: React.FC<{
  page: number
  pageCount: number
  onPageChange: (page: number) => void
}> = ({ page, pageCount, onPageChange }) => {
  const total = Math.max(1, pageCount)
  const current = Math.min(Math.max(0, page), total - 1)
  const showEndButtons = total >= 3
  return (
    <Stack direction="row" alignItems="center" gap={0.25}>
      {showEndButtons ? (
        <IconButton
          onClick={() => onPageChange(0)}
          disabled={current === 0}
          aria-label="First page"
          sx={PAGINATION_BUTTON_SX}
        >
          <FirstPageIcon fontSize="small" />
        </IconButton>
      ) : null}
      <IconButton
        onClick={() => onPageChange(current - 1)}
        disabled={current === 0}
        aria-label="Previous page"
        sx={PAGINATION_BUTTON_SX}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          px: 1,
          fontSize: '0.8125rem',
        }}
      >
        Page {current + 1} of {total}
      </Typography>
      <IconButton
        onClick={() => onPageChange(current + 1)}
        disabled={current >= total - 1}
        aria-label="Next page"
        sx={PAGINATION_BUTTON_SX}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
      {showEndButtons ? (
        <IconButton
          onClick={() => onPageChange(total - 1)}
          disabled={current >= total - 1}
          aria-label="Last page"
          sx={PAGINATION_BUTTON_SX}
        >
          <LastPageIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Stack>
  )
}

// "Showing X-Y of N" range readout. Hides itself when nothing matches
// (totalCount === 0) so the page-size + pagination cluster doesn't sit
// alongside a "Showing 1-0 of 0" non sequitur. Rendered a half-shade
// darker than `text.secondary` so the metadata group reads as one
// clear band, not a faint whisper next to the pill.
const ResultRange: React.FC<{
  page: number
  rowsPerPage: number
  totalCount: number
}> = ({ page, rowsPerPage, totalCount }) => {
  if (totalCount <= 0) return null
  const start = page * rowsPerPage + 1
  const end = Math.min((page + 1) * rowsPerPage, totalCount)
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{
        color: 'rgba(0, 0, 0, 0.72)',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        fontSize: '0.875rem',
        lineHeight: 1.4,
      }}
    >
      Showing {start.toLocaleString()}-{end.toLocaleString()} of{' '}
      {totalCount.toLocaleString()}
    </Typography>
  )
}

const ResultsToolbar = React.forwardRef<HTMLDivElement, ResultsToolbarProps>(
  function ResultsToolbar(
    {
      isInspector,
      toolbarStickyTop,
      chips,
      title,
      leftSlot,
      totalCount,
      rowsPerPage,
      page,
      pageCount,
      pageSizeOptions,
      showPagination,
      rightSlot,
      isLoading,
      onRowsPerPageChange,
      onPageChange,
      statusToggle,
      oldestOpenChip,
    },
    ref,
  ) {
    return (
      <Box
        ref={isInspector ? ref : undefined}
        sx={
          isInspector
            ? {
                position: 'sticky',
                top: toolbarStickyTop,
                zIndex: Z_TOOLBAR,
                // Sits inside the outer Results Paper which is all-white.
                // No bg colour, no border, no rounded corners of its own -
                // the inner table card (below) provides the visual
                // separation, and the Paper's outer curve handles the
                // top corners of the section.
                backgroundColor: 'background.paper',
                // Slide in lock-step with the app chrome collapse
                // animation (see InspectorLayout - the brand bar
                // collapses over 200ms, and `--app-chrome-height` shrinks
                // with it so our `top:` value here drops smoothly).
                transition: 'top 200ms ease',
              }
            : {
                // Platform reports header band - light grey surface with
                // a hairline divider so the per-group title + count chip
                // (in leftSlot) read as a clear "Results header" sitting
                // flush above the teal column header row.
                backgroundColor: 'grey.50',
                borderBottom: `1px solid ${TOOLBAR_DIVIDER}`,
              }
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
          flexWrap="wrap"
          sx={
            isInspector
              ? {
                  // Compact SaaS table toolbar chrome: ~48px row, 16px
                  // horizontal padding, items vertically centred. One
                  // stable height — state-dependent padding caused a
                  // measurement lag against the group/column-header
                  // sticky offsets, which surfaced as body rows showing
                  // through under the toolbar in the matrix variant
                  // when sticky kicked in.
                  //
                  // Asymmetric vertical padding: 2px top / 4px bottom
                  // sits the controls slightly above optical centre,
                  // which reads as tighter against the page-header
                  // above without changing the 48px minHeight.
                  px: 2,
                  pt: 0.25,
                  pb: 0.5,
                  minHeight: 48,
                  rowGap: 0.5,
                }
              : { px: 2, py: 1, rowGap: 1 }
          }
        >
          {/* LEFT - Title + caller-supplied `leftSlot` + lifecycle pill
            * inline on one row. Active-filter chips live in the Filter
            * issues card now; the sticky toolbar stays a single
            * compact row regardless of how many filters are active.
            * Platform variant keeps the inline chips since its toolbar
            * is non-sticky and the chips read as part of the page-header
            * band there. */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              flexWrap: 'wrap',
              columnGap: 1.25,
              rowGap: 0.5,
              minWidth: 0,
            }}
          >
            {isInspector && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {title ?? 'Results'}
              </Typography>
            )}
            {leftSlot}
            {statusToggle}
            {!isInspector && <ActiveFilterChips chips={chips} />}
          </Stack>

          {/* RIGHT - Designed table control group: a metadata band
            * ("Oldest 10d | Showing X-Y of N") split by a vertical
            * hairline, followed by the page-size outlined select,
            * prev/next pagination, then an outlined expand button
            * separated by a small vertical divider. Oldest-open sits
            * here (and not beside the lifecycle pill on the left) so
            * it reads as result metadata, not another filter.
            * `gap=3` (24px) between groups so the metadata band reads
            * distinctly from the page-size + pagination cluster. */}
          <Stack direction="row" alignItems="center" gap={3} flexWrap="wrap">
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                minHeight: 32,
              }}
            >
              {oldestOpenChip}
              <ResultRange
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
              />
            </Box>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) =>
                onRowsPerPageChange(parseInt(String(e.target.value), 10))
              }
              renderValue={(v) => `${v} per page`}
              sx={{
                height: 32,
                fontSize: '0.8125rem',
                color: 'text.primary',
                backgroundColor: 'background.paper',
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.16)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.32)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: poolwerxColors.teal,
                  borderWidth: 1,
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                  pl: 1.25,
                  pr: '28px !important',
                },
              }}
            >
              {pageSizeOptions.map((n) => (
                <MenuItem key={n} value={n}>
                  {n} per page
                </MenuItem>
              ))}
            </Select>
            {showPagination && (
              <CompactPagination
                page={page}
                pageCount={pageCount}
                onPageChange={onPageChange}
              />
            )}
            {rightSlot && (
              <Stack direction="row" alignItems="center" gap={1}>
                <ControlGroupDivider />
                {rightSlot}
              </Stack>
            )}
          </Stack>
        </Stack>
        {/* Thin progress strip - pinned with the toolbar so it stays
          * visible during refetches. */}
        {isLoading && (
          <LinearProgress
            sx={{
              height: 2,
              borderRadius: 0,
              backgroundColor: 'rgba(0, 127, 152, 0.12)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: poolwerxColors.teal,
              },
            }}
          />
        )}
      </Box>
    )
  },
)

export default ResultsToolbar
